/**
 * P-04 — small pure helpers of the project screens (form, page, list).
 * The rules themselves (progress, phase, planned hours, health) are the
 * server's (projectMetrics.service); the screens only label them.
 */
import type { Tone } from '../ui/tone';
import type { CorePhase, ProjectHealthState } from '../../types';

/** D-06 — the fixed payment schedule of a type (i18n key of its text). */
export function scheduleKey(kind: string | null | undefined): string {
  return kind === 'development' ? 'projectForm.scheduleDevelopment' : 'projectForm.scheduleProject';
}

/** A5 (D-40) — the tone of a health state. */
export const HEALTH_TONE: Readonly<Record<ProjectHealthState, Tone>> = {
  late: 'danger',
  over_hours: 'danger',
  attention: 'warning',
  ok: 'success',
};

export const CORE_PHASES: readonly CorePhase[] = ['development', 'qa', 'delivery', 'fixes'];

/** Statuses a project is SET to by "שינוי סטטוס" ("בוטל" only through "ביטול הפרויקט"). */
export const CHANGEABLE_STATUSES = ['planning', 'active', 'on_hold', 'completed'] as const;

/** Hours as shown: one decimal, no trailing ".0". */
/**
 * P-30c (D-175, QX-40) — the hover text of a task's "שעות בפועל":
 * "12 שעות (מתוכן 3 לא לחיוב)"; with nothing non-billable, just "12 שעות".
 */
export function actualHoursTitle(
  actual: number | string | null | undefined,
  nonBillable: number | string | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const nb = Number(nonBillable);
  const total = hoursText(actual);
  if (!Number.isFinite(nb) || nb <= 0) return t('taskPage.actualHoursPlain', { total });
  return t('taskPage.actualHoursBreakdown', { total, nonBillable: hoursText(nb) });
}

export function hoursText(n: number | string | null | undefined): string {
  const v = Number(n);
  if (!Number.isFinite(v)) return '0';
  return String(Math.round(v * 10) / 10);
}

// ---------------------------------------------------------------------------
// D-303 / D-304 — "אחריות ללקוח" of a project / development: a date, or "אין אחריות".
// ---------------------------------------------------------------------------

export interface WarrantyValue { until: string; none: boolean }
export interface WarrantyPatch { warranty_until: string | null; no_warranty: boolean }

/** The value of a project row (GET /projects/:id): the date as YYYY-MM-DD, "אין אחריות" as a flag. */
export function warrantyOf(row: { warranty_until?: unknown; no_warranty?: unknown } | null | undefined): WarrantyValue {
  const u = row?.warranty_until;
  return { until: typeof u === 'string' ? u.slice(0, 10) : '', none: row?.no_warranty === true };
}

/** Ending a project / development needs one of the two (QX-1313). */
export function warrantyMissing(v: WarrantyValue): boolean {
  return !v.none && !v.until;
}

/** The body fields for the server: "אין אחריות" clears the date, a date clears "אין אחריות". */
export function warrantyPatch(v: WarrantyValue): WarrantyPatch {
  return v.none ? { warranty_until: null, no_warranty: true } : { warranty_until: v.until || null, no_warranty: false };
}

/** The list row as it is AFTER a status change (the undo of it re-enters the old status from this one). */
export function rowAfterStatus<T extends { child_status?: string | null }>(row: T, status: string): T {
  return { ...row, child_status: status };
}

/** Does moving a project / development from `from` to `to` need the warranty answer? */
export function needsWarrantyOnStatus(kind: string | null | undefined, from: string | null | undefined, to: string): boolean {
  return (kind === 'project' || kind === 'development') && to === 'completed' && from !== 'completed';
}
